import { httpService } from "./HttpService";
import { getStoredUser } from "../utils/auth";
import { isRole, UserRoleEnum } from "@/types/role.type";
export interface Study {
  id: string;
  name: string;
  tenant?: {
    id: string;
    name?: string;
  };
}
export interface Role {
  id: string;
  internal_role: string;
  description?: string;
}
export interface UserInvitationRequest {
  email?: string;
  invite_metadata: {
    inviteeName?: string;
    tenantId: string;
    tenantDetails?: {
      name?: string;
      description?: string;
      logoUrl?: string;
      country?: string;
      deploymentType?: string;
      contactEmail?: string;
      contactPhone?: string;
      licenseDetails?: {
        licenseKey?: string;
        expirationDate?: string;
        maxUsers?: number;
        maxStudies?: number;
      };
    };
    roles: {
      roleId: string;
      studyId: string;
      siteId?: string;
    }[];
  };
}
export interface UserInvitationResponse {
  id: string;
  email: string;
  invite_token: string;
  invite_metadata: UserInvitationRequest["invite_metadata"];
  status: "PENDING" | "ACCEPTED" | "EXPIRED";
  expiresAt: string;
  createdAt: string;
}
export interface InviteDetailsResponse {
  id: string;
  email: string;
  // Note: inviteToken is NOT included in list/detail responses for security
  // It's only returned when creating an invitation
  inviteMetadata: {
    inviteeName?: string;
    roles?: {
      roleId: string;
      siteId?: string;
      studyId: string;
    }[];
    tenantId?: string;
    tenantDetails?: {
      name: string;
      country?: string;
      logoUrl?: string;
      description?: string;
      contactEmail: string;
      contactPhone?: string;
      deploymentType?: string;
      licenseDetails?: {
        maxUsers?: number;
        licenseKey?: string;
        maxStudies?: number;
        expirationDate?: string;
      };
    };
  };
  isUsed: boolean;
  active: boolean;
  inviteStatus?: "INVITED" | "EXPIRED" | "WITHDRAWN" | "ACCEPTED";
  tenantId: string;
  createdAt: string;
  usedAt: string | null;
  expiresAt: string;
}
export interface ApiResponse<T> {
  data: T;
  message?: string;
  success: boolean;
}
export const invitationService = {
  async getStudies(): Promise<Study[]> {
    try {
      // Get all studies (they will be filtered by tenant on the backend)
      const res = await httpService.get<Study[]>("/studies");
      if (res.data && res.data.length > 0) {
        return res.data.map((study) => ({
          id: study.id,
          name: study.name,
          tenant: undefined,
        }));
      } else {
        return [];
      }
    } catch {
      return [];
    }
  },
  async getRoles(): Promise<Role[]> {
    try {
      const res = await httpService.get<Role[]>("/roles/getAll");
      if (res.data && res.data.length > 0) {
        return res.data.filter(
          (role) => !isRole(role.internal_role, UserRoleEnum.TENANT),
        );
      }
      return [];
    } catch {
      return [];
    }
  },
  async checkEmailExists(email: string): Promise<boolean> {
    try {
      const res = await httpService.get<{
        exists: boolean;
      }>("/users/check-email", {
        params: { email },
      });
      return res.data.exists;
    } catch {
      return false; // Don't block UI if endpoint not available
    }
  },
  async createInvitation(
    payload: UserInvitationRequest,
  ): Promise<UserInvitationResponse> {
    const res = await httpService.post<UserInvitationResponse>(
      "/invites/create",
      payload,
    );
    return res.data;
  },
  async updateInvitation(
    invitationId: string,
    payload: UserInvitationRequest,
  ): Promise<UserInvitationResponse> {
    const res = await httpService.patch<UserInvitationResponse>(
      `/invites/${invitationId}/update-resend`,
      payload,
    );
    return res.data;
  },
  async revokeInvitation(invitationId: string): Promise<{
    message: string;
    success: boolean;
  }> {
    const res = await httpService.delete<{
      message: string;
      success: boolean;
    }>(`/invites/${invitationId}`);
    return res.data;
  },
  async resendInvitation(invitationId: string): Promise<{
    message: string;
    email: string;
    inviteId: string;
  }> {
    const res = await httpService.post<{
      message: string;
      email: string;
      inviteId: string;
    }>(`/invites/${invitationId}/resend`);
    return res.data;
  },
  // Note: There's no endpoint to get invites by email
  // We'll use getInviteById with the invite ID directly
  // For users with "Pending Invitation" status, the user.id might be the invite ID
  // or we need to get it from another source
  async getInviteById(inviteId: string): Promise<InviteDetailsResponse | null> {
    try {
      const res = await httpService.get<InviteDetailsResponse>(
        `/invites/${inviteId}`,
      );
      if (res?.data) {
        return res.data;
      }
      return null;
    } catch {
      return null;
    }
  },
  /**
   * Get all invites for the current tenant
   * Endpoint: GET /api/v1/invites/tenant/list
   * Returns invites filtered by tenant ID from the current user
   */
  async getInvitesByTenant(): Promise<InviteDetailsResponse[]> {
    try {
      const currentUser = getStoredUser();
      if (!currentUser?.tenantId) {
        return [];
      }
      const res = await httpService.get<InviteDetailsResponse[]>(
        "/invites/tenant/list",
        {
          params: {
            includeInactive: true,
          },
        },
      );
      if (res.data && Array.isArray(res.data)) {
        return res.data;
      } else {
        return [];
      }
    } catch {
      return [];
    }
  },
};
